import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../../api/client';
import type { GuestSearchResult, GuestDetail } from './types';

// --- Styles ---

const containerStyle: React.CSSProperties = {
  padding: '16px',
  paddingBottom: '80px',
  maxWidth: '768px',
  margin: '0 auto',
};

const headingStyle: React.CSSProperties = {
  fontSize: '20px',
  fontWeight: 700,
  margin: '0 0 12px 0',
  color: '#1a1a1a',
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '44px',
  padding: '10px 14px',
  fontSize: '16px',
  border: '1px solid #ccc',
  borderRadius: '8px',
  boxSizing: 'border-box',
  outline: 'none',
};

const resultListStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: '12px 0 0 0',
  padding: 0,
};

const resultItemStyle: React.CSSProperties = {
  padding: '12px',
  borderBottom: '1px solid #e5e5e5',
  cursor: 'pointer',
  minHeight: '44px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const resultNameStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#1a1a1a',
};

const resultMetaStyle: React.CSSProperties = {
  fontSize: '13px',
  color: '#555',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const statusBadgeBase: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '24px',
  color: '#666',
  fontSize: '14px',
};

const emptyStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '24px',
  color: '#888',
  fontSize: '14px',
};

const errorStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '16px',
  color: '#c0392b',
  fontSize: '14px',
};

// Detail overlay styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: '#fff',
  zIndex: 900,
  overflowY: 'auto',
  padding: '16px',
  paddingBottom: '80px',
};

const backButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  minHeight: '44px',
  minWidth: '44px',
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: 500,
  color: '#0a1f14',
  marginBottom: '8px',
};

const detailHeadingStyle: React.CSSProperties = {
  fontSize: '22px',
  fontWeight: 700,
  margin: '0 0 4px 0',
  color: '#1a1a1a',
};

const detailSubheadingStyle: React.CSSProperties = {
  fontSize: '14px',
  color: '#666',
  margin: '0 0 16px 0',
};

const detailSectionStyle: React.CSSProperties = {
  marginBottom: '16px',
};

const detailSectionTitleStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#888',
  marginBottom: '6px',
};

const detailRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 0',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
};

const detailLabelStyle: React.CSSProperties = {
  color: '#555',
  fontWeight: 500,
};

const detailValueStyle: React.CSSProperties = {
  color: '#1a1a1a',
  fontWeight: 600,
  textAlign: 'right',
};

const companionListStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
};

const companionItemStyle: React.CSSProperties = {
  padding: '6px 0',
  borderBottom: '1px solid #f0f0f0',
  fontSize: '14px',
  color: '#1a1a1a',
};

// --- Component ---

export function GuestLookupPanel() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GuestSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Detail view state
  const [selectedGuest, setSelectedGuest] = useState<GuestDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  const performSearch = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setResults([]);
      setHasSearched(false);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const data = await apiClient<GuestSearchResult[]>(
        `/table-management/guests?query=${encodeURIComponent(searchTerm.trim())}`,
      );
      setResults(data);
      setHasSearched(true);
    } catch {
      setSearchError('Unable to reach server');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setResults([]);
      setHasSearched(false);
      setSearchError(null);
      return;
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Fetch guest detail
  const handleGuestSelect = async (guestId: string) => {
    setIsLoadingDetail(true);
    setDetailError(null);

    try {
      const detail = await apiClient<GuestDetail>(
        `/table-management/guests/${guestId}`,
      );
      setSelectedGuest(detail);
    } catch {
      setDetailError('Unable to load guest details');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleBack = () => {
    setSelectedGuest(null);
    setDetailError(null);
  };

  // Status badge color
  const getStatusColor = (status: GuestSearchResult['checkInStatus']) => {
    switch (status) {
      case 'CheckedIn':
        return { background: '#d4edda', color: '#155724' };
      case 'Assigned':
        return { background: '#fff3cd', color: '#856404' };
      default:
        return { background: '#e2e3e5', color: '#383d41' };
    }
  };

  // --- Detail Overlay ---
  if (selectedGuest || isLoadingDetail || detailError) {
    return (
      <div style={overlayStyle}>
        <button
          type="button"
          style={backButtonStyle}
          onClick={handleBack}
          aria-label="Back to results"
        >
          ← Back
        </button>

        {isLoadingDetail && (
          <div style={loadingStyle}>Loading guest details…</div>
        )}

        {detailError && !isLoadingDetail && (
          <div style={errorStyle}>{detailError}</div>
        )}

        {selectedGuest && !isLoadingDetail && (
          <div>
            <h2 style={detailHeadingStyle}>{selectedGuest.fullName}</h2>
            <p style={detailSubheadingStyle}>
              Ticket: {selectedGuest.ticketNumber}
              {(selectedGuest as any).registration?.ticket?.tierName && ` · ${(selectedGuest as any).registration.ticket.tierName}`}
              {selectedGuest.chapterAffiliation && ` · ${selectedGuest.chapterAffiliation}`}
            </p>

            {/* Order Info */}
            {(selectedGuest as any).registration?.ticket?.order?.referenceId && (
              <div style={detailSectionStyle}>
                <div style={detailRowStyle}>
                  <span style={detailLabelStyle}>Order</span>
                  <span style={detailValueStyle}>{(selectedGuest as any).registration.ticket.order.referenceId}</span>
                </div>
              </div>
            )}

            {/* Status Section */}
            <div style={detailSectionStyle}>
              <div style={detailSectionTitleStyle}>Status</div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>RSVP Status</span>
                <span style={detailValueStyle}>{selectedGuest.rsvpStatus}</span>
              </div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Payment Status</span>
                <span style={detailValueStyle}>{selectedGuest.paymentStatus}</span>
              </div>
            </div>

            {/* Seating Section */}
            <div style={detailSectionStyle}>
              <div style={detailSectionTitleStyle}>Seating</div>
              {selectedGuest.seat ? (
                <>
                  <div style={detailRowStyle}>
                    <span style={detailLabelStyle}>Table</span>
                    <span style={detailValueStyle}>{selectedGuest.seat.tableNumber}</span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={detailLabelStyle}>Seat</span>
                    <span style={detailValueStyle}>{selectedGuest.seat.seatNumber}</span>
                  </div>
                  <div style={detailRowStyle}>
                    <span style={detailLabelStyle}>Zone</span>
                    <span style={detailValueStyle}>{selectedGuest.seat.zoneName}</span>
                  </div>
                </>
              ) : (
                <div style={{ ...detailRowStyle, borderBottom: 'none' }}>
                  <span style={detailLabelStyle}>No seat assigned</span>
                </div>
              )}
              {selectedGuest.seatingPreferences && (
                <div style={detailRowStyle}>
                  <span style={detailLabelStyle}>Preferences</span>
                  <span style={detailValueStyle}>{selectedGuest.seatingPreferences}</span>
                </div>
              )}
            </div>

            {/* Needs & Preferences Section */}
            <div style={detailSectionStyle}>
              <div style={detailSectionTitleStyle}>Needs & Preferences</div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Accessibility Needs</span>
                <span style={detailValueStyle}>
                  {selectedGuest.accessibilityNeeds || 'None'}
                </span>
              </div>
              <div style={detailRowStyle}>
                <span style={detailLabelStyle}>Dietary Preference</span>
                <span style={detailValueStyle}>
                  {selectedGuest.dietaryPreference || 'None'}
                </span>
              </div>
            </div>

            {/* Companions Section */}
            <div style={detailSectionStyle}>
              <div style={detailSectionTitleStyle}>Companions</div>
              {selectedGuest.companions.length > 0 ? (
                <ul style={companionListStyle}>
                  {selectedGuest.companions.map((companion) => (
                    <li key={companion.id} style={companionItemStyle}>
                      {companion.fullName}
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ fontSize: '14px', color: '#888', padding: '6px 0' }}>
                  No companions
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Search & Results View ---
  return (
    <div style={containerStyle}>
      <h1 style={headingStyle}>Guest Lookup</h1>

      <input
        type="search"
        style={searchInputStyle}
        placeholder="Search by name, ticket number…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search guests"
      />

      {isSearching && <div style={loadingStyle}>Searching…</div>}

      {searchError && <div style={errorStyle}>{searchError}</div>}

      {!isSearching && !searchError && hasSearched && results.length === 0 && (
        <div style={emptyStyle}>No guests found</div>
      )}

      {results.length > 0 && (
        <ul style={resultListStyle} aria-label="Guest search results">
          {results.map((guest) => {
            const statusColors = getStatusColor(guest.checkInStatus);
            return (
              <li
                key={guest.id}
                style={resultItemStyle}
                onClick={() => handleGuestSelect(guest.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleGuestSelect(guest.id);
                  }
                }}
                aria-label={`View details for ${guest.fullName}`}
              >
                <span style={resultNameStyle}>{guest.fullName}</span>
                <span style={resultMetaStyle}>
                  <span>🎫 {guest.ticketNumber}</span>
                  {guest.chapterAffiliation && (
                    <span>📍 {guest.chapterAffiliation}</span>
                  )}
                  {guest.tableNumber != null && guest.seatNumber != null && (
                    <span>🪑 Table {guest.tableNumber}, Seat {guest.seatNumber}</span>
                  )}
                  {guest.checkInStatus && (
                    <span
                      style={{
                        ...statusBadgeBase,
                        background: statusColors.background,
                        color: statusColors.color,
                      }}
                    >
                      {guest.checkInStatus === 'CheckedIn' ? 'Checked In' : guest.checkInStatus}
                    </span>
                  )}
                  {guest.dietaryPreference && (
                    <span>🍽️ {guest.dietaryPreference}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
